import { Icon } from '@toggle/design-system';
import { formatPercentage } from '@toggle/helpers';
import { EventFeedEvent } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { config } from '~/config/app.config';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { getTierRelationText } from '~/hooks/use-insight-drawer/utils';
import { getCardDate } from '~/utils/card-utils/card-utils';
import { CardPill } from '~/views/stream/components/stream-card/components/card-pill/CardPill';
import { CardType } from '~/views/stream/components/stream-card/StreamCard';

import styles from '../../OverviewEventFeed.module.scss';

export interface ScenarioRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
}

export const ScenarioRow = ({ event, isLast }: ScenarioRowProps) => {
  const { t } = useTranslation(['whatsNew', 'insights']);
  const { openDrawer } = useInsightDrawer();

  const median = event.metadata.median_return ?? 0;
  const horizon = event.metadata.horizon;
  const stars = event.metadata.stars;
  const episodeCount = event.metadata.episode_count ?? 0;
  const isBullish = median >= 0;
  const eventDate = new Date(event.created_at);

  const relation = getTierRelationText(event.relevance.tier, t);

  const handleClick = () => {
    if (config.app.isTeamsApp) {
      return;
    }

    openDrawer({
      id: event.source_id,
      entityTag: event.entity,
      trackingProperties: { source: 'asset_overview' },
      relation,
    });
  };

  return (
    <div
      data-testid="scenario-row"
      className={clsx(styles.feedItemWrapper, {
        [styles.last]: isLast,
        [styles.notClickable]: config.app.isTeamsApp,
      })}
    >
      <div className={styles.feedItem} onClick={handleClick}>
        <div className={styles.feedItemHeader}>
          <CardPill type={CardType.Scenario} />
        </div>
        <p className={styles.insightTitle}>{event.title}</p>
        <div className={styles.statsRow}>
          <div className={styles.statsContainer}>
            {isBullish ? (
              <span className={styles.bullishValue}>
                {formatPercentage(median, { suffix: '%', addPlus: true })}
              </span>
            ) : (
              <span className={styles.bearishValue}>
                {formatPercentage(median, { suffix: '%', addPlus: true })}
              </span>
            )}
            {horizon && (
              <>
                <div className={styles.dot} />
                <span className={styles.statValue}>
                  {horizon.toUpperCase()}
                </span>
              </>
            )}
            {stars !== undefined && (
              <>
                <div className={styles.dot} />
                <div className={styles.starsWrapper}>
                  <Icon
                    iconName="Rating"
                    size={10}
                    fillColor="var(--icon-primary)"
                  />
                  <span className={styles.starsLabel}>{stars}</span>
                </div>
              </>
            )}
            <div className={styles.verticalDivider} />
            <span className={styles.episodesLabel}>
              {t('insights:insightCard.episode', { count: episodeCount })}
            </span>
          </div>
          <span className={styles.timeAgo}>{getCardDate(eventDate)}</span>
        </div>
      </div>
    </div>
  );
};
